import React, { useState, useEffect, useRef } from "react";
import { ArrowUp, Crown, Users, Gift, X, Check, CheckCircle } from "lucide-react";
import axios from "axios";
import DashboardVisual from "./DashboardVisual";
import LoginModal from "./LoginModal";
import FeedbackToast from "./FeedbackToast";
import AdminDashboard from "./AdminDashboard";
import useAdminUnread from "./useAdminUnread";
import T from "./translations";
import legal, { LEGAL_VERSION } from "./legal";

// Rend un document juridique depuis legal.js. Le texte porte quelques balises de
// mise en forme (<strong>, <em>) : elles viennent d'un fichier du dépôt, jamais
// d'une saisie utilisateur, donc l'injection HTML est sans risque ici.
function LegalDocument({ doc, lang }) {
  const pack = legal[lang] || legal.fr;
  const sections = pack[doc] || [];
  return (
    <div className="legal-content">
      <p className="legal-date">{pack.updated(LEGAL_VERSION)}</p>
      {sections.map((section, i) => (
        <React.Fragment key={i}>
          <h3>{section.title}</h3>
          {section.blocks.map((block, j) =>
            block.type === "ul" ? (
              <ul key={j}>
                {block.items.map((item, k) => (
                  <li key={k} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ul>
            ) : (
              <p key={j} dangerouslySetInnerHTML={{ __html: block.text }} />
            )
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

// Identifiant de navigateur du quota anonyme. Le backend comptait les analyses
// gratuites par adresse IP : derriere un operateur mobile ou le reseau d'une
// entreprise, des milliers de personnes partagent une IPv4 et se refusaient
// mutuellement leur premiere analyse. Il est genere une fois, garde ici, et
// hache cote serveur — l'IP ne borne plus que l'abus massif.
const visitorId = () => {
  try {
    let identifiant = localStorage.getItem("rms_vid");
    if (!identifiant) {
      identifiant = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem("rms_vid", identifiant);
    }
    return identifiant;
  } catch {
    // Stockage bloque : le serveur retombe alors sur le comptage par IP.
    return "";
  }
};

const REGIONS = [
  { value: "euw",  label: "EUW — Europe West" },
  { value: "eun",  label: "EUNE — Europe Nordic & East" },
  { value: "na",   label: "NA — North America" },
  { value: "kr",   label: "KR — Korea" },
  { value: "br",   label: "BR — Brazil" },
  { value: "jp",   label: "JP — Japan" },
  { value: "lan",  label: "LAN — Latin America North" },
  { value: "las",  label: "LAS — Latin America South" },
  { value: "oce",  label: "OCE — Oceania" },
  { value: "tr",   label: "TR — Turkey" },
  { value: "ru",   label: "RU — Russia" },
];

function ContactModal({ token, user, onClose, showToast, t }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/message`, { subject: subject.trim(), message: message.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSent(true);
    } catch {
      showToast(t("contact_error"));
    }
    setSending(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: '26.25rem' }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><X size={16} /></button>
        <h2 className="modal-title">{t("contact_modal_title")}</h2>

        {sent ? (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✓</div>
            <p style={{ color: "#4caf50", fontWeight: 700, marginBottom: '0.5rem' }}>{t("contact_sent_title")}</p>
            <p style={{ fontSize: '0.8125rem', color: "var(--muted)" }}>{t("contact_sent_sub")}</p>
          </div>
        ) : user ? (
          <div style={{ display: "flex", flexDirection: "column", gap: '0.625rem' }}>
            <p style={{ fontSize: '0.8125rem', color: "var(--muted)", margin: 0 }}>
              {t("contact_modal_sub")}
            </p>
            <input
              className="admin-filter-input"
              style={{ marginBottom: 0 }}
              placeholder={t("contact_subject_placeholder")}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <textarea
              style={{ background: "var(--card)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: '0.5rem', color: "#e2e8f0", padding: "0.625rem 0.75rem", fontSize: '0.8125rem', resize: "vertical", minHeight: '7.5rem', fontFamily: "inherit" }}
              placeholder={t("contact_message_placeholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <button
              className="cta cta-primary"
              style={{ opacity: (!message.trim() || sending) ? 0.5 : 1 }}
              onClick={handleSend}
              disabled={!message.trim() || sending}
            >
              {sending ? t("contact_sending") : t("contact_send")}
            </button>
          </div>
        ) : (
          <>
            <p style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: '1rem', textAlign: "center" }}>{t("contact_modal_sub")}</p>
            <div className="share-link-box">
              <span className="share-link-text">contact@roast-my-stats.com</span>
              <button className="cta cta-ghost" style={{ fontSize: '0.75rem', padding: "0.375rem 0.75rem", flexShrink: 0 }}
                onClick={() => { navigator.clipboard.writeText("contact@roast-my-stats.com"); showToast(t("toast_email_copied")); }}>
                {t("copy")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Lancement Product Hunt du 13 septembre 2026 — TERMINE, sans effet mesurable :
// total_reports est reste a 229, la valeur de la veille. Tout l'habillage est
// eteint depuis le 13 septembre : PH_FIN est anterieur a PH_DEBUT, donc phasePH()
// renvoie null en permanence et ni le bandeau ni le badge ne sont rendus.
//
// POUR UN PROCHAIN LANCEMENT : remettre les deux dates, decommenter le badge dans
// le footer, rouvrir img-src a api.producthunt.com dans vercel.json et redeclarer
// Product Hunt dans legal.js. Les quatre vont ensemble — le badge seul serait
// bloque en silence par la CSP.
const PH_URL = "https://www.producthunt.com/products/roast-my-stats";
const PH_DEBUT = Date.parse("2026-09-13T10:00:00Z");
const PH_FIN = Date.parse("2026-09-13T00:00:00Z");

// Renvoie "live", "soon" ou null. La phase sert aussi de cle de fermeture : fermer
// le bandeau d'annonce ne masque pas celui du jour J.
function phasePH() {
  const t = Date.now();
  if (t >= PH_FIN) return null;
  return t >= PH_DEBUT ? "live" : "soon";
}

function App() {
  const [region, setRegion] = useState("euw");
  // Pose par le lien `?pass=1` du recap hebdomadaire, consomme par l'effet qui
  // suit `handleSubscribe` : celui-ci n'existe pas encore quand l'URL est lue.
  const [pendingPass, setPendingPass] = useState(false);
  const [pseudo, setPseudo] = useState("");
  const [report, setReport] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboardPage, setShowLeaderboardPage] = useState(
    window.location.pathname === "/leaderboard"
  );
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [error, setError] = useState("");
  const [previousReport, setPreviousReport] = useState(null);
  const [lbSubmitInput, setLbSubmitInput] = useState("");
  const [lbSubmitDone, setLbSubmitDone] = useState(false);

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("rms_token"));
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("rms_lang");
    if (stored) return stored;
    return navigator.language?.startsWith("fr") ? "fr" : "en";
  });
  const t = (key, ...args) => {
    const val = T[lang]?.[key] ?? T.fr?.[key] ?? key;
    return typeof val === "function" ? val(...args) : val;
  };
  const setLanguage = (l) => {
    setLang(l);
    localStorage.setItem("rms_lang", l);
    if (token) {
      axios.put(`${API}/auth/language`, { lang: l }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };
  const [showLogin, setShowLogin] = useState(false);
  // Onglet sur lequel ouvrir la LoginModal : "register" quand le CTA promet un compte
  const [loginTab, setLoginTab] = useState("login");
  const [hasRefBonus, setHasRefBonus] = useState(false);
  const [phFerme, setPhFerme] = useState(() => {
    try { return localStorage.getItem("rms_ph_banner") || ""; } catch { return ""; }
  });
  const [history, setHistory] = useState([]);
  const [searchesRemaining, setSearchesRemaining] = useState(null);
  // Le plafond depend de qui regarde (3 sans compte, 5 en gratuit) : il est
  // annonce par le serveur plutot que code en dur dans la jauge.
  const [searchesLimit, setSearchesLimit] = useState(5);
  const [lastSearch, setLastSearch] = useState(null); // { pseudo, region, mock }
  const lastSearchRef = useRef(null);
  const [searchHistory, setSearchHistory] = useState(
    () => {
      const raw = JSON.parse(localStorage.getItem("rms_pseudo_history") || "[]");
      // Migration: anciens formats string → objet
      return raw.map((p) => typeof p === "string" ? { pseudo: p, region: "euw" } : p);
    }
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const pseudoInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const upsellCooldownRef = useRef(0);
  const upsellIdxRef = useRef(0);
  const userRef = useRef(null);
  const tryShowUpsellRef = useRef(null);
  // Page de partage : le visiteur consulte la carte de quelqu'un d'autre, il n'a
  // rien analyse. Les relances « Tu as analyse N parties » y etaient donc fausses.
  const readOnlyRef = useRef(false);
  const [upsellNotif, setUpsellNotif] = useState(null);

  // `premium` est calcule par le backend depuis le 2026-09-09 : le recomposer ici
  // laissait une dizaine d'endroits libres de se tromper.
  const isPremium = !!user?.premium;

  const saveToSearchHistory = (value, reg) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSearchHistory((prev) => {
      const filtered = prev.filter((p) => p.pseudo.toLowerCase() !== trimmed.toLowerCase());
      const next = [{ pseudo: trimmed, region: reg }, ...filtered].slice(0, 10);
      localStorage.setItem("rms_pseudo_history", JSON.stringify(next));
      return next;
    });
  };

  const removeFromSearchHistory = (pseudo) => {
    const deleted = JSON.parse(localStorage.getItem("rms_pseudo_deleted") || "[]");
    if (!deleted.includes(pseudo.toLowerCase())) {
      localStorage.setItem("rms_pseudo_deleted", JSON.stringify([...deleted, pseudo.toLowerCase()]));
    }
    setSearchHistory((prev) => {
      const next = prev.filter((p) => p.pseudo !== pseudo);
      localStorage.setItem("rms_pseudo_history", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => { userRef.current = user; }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !pseudoInputRef.current?.contains(e.target) &&
        !suggestionsRef.current?.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTrophies = (tk) => {
    axios.get(`${API}/trophies`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((res) => setTrophies(res.data))
      .catch(() => setTrophies(null));
  };

  const fetchHistory = (authToken) => {
    axios.get(`${API}/history`, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then((res) => setHistory(res.data)).catch(() => {});
    axios.get(`${API}/suggestions`, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then((res) => {
      if (res.data.length > 0) {
        const deleted = new Set(JSON.parse(localStorage.getItem("rms_pseudo_deleted") || "[]"));
        setSearchHistory((prev) => {
          const filtered = res.data.filter((s) => !deleted.has(s.pseudo.toLowerCase()));
          const serverPseudos = new Set(filtered.map((s) => s.pseudo.toLowerCase()));
          const merged = [
            ...filtered,
            ...prev.filter((p) => !serverPseudos.has(p.pseudo.toLowerCase()) && !deleted.has(p.pseudo.toLowerCase())),
          ].slice(0, 10);
          localStorage.setItem("rms_pseudo_history", JSON.stringify(merged));
          return merged;
        });
      }
    }).catch(() => {});
  };

  // Vérifier le token au démarrage et après retour Stripe
  const fetchLeaderboard = () => {
    axios.get(`${API}/leaderboard`)
      .then((res) => setLeaderboard(res.data))
      .catch(() => {});
  };

  const fetchCommunity = () => {
    axios.get(`${API}/community`)
      .then((res) => setCommunity(res.data))
      .catch(() => {});
  };

  const handleTogglePublish = async () => {
    try {
      const body = { report: report || null };
      const res = await axios.put(`${API}/auth/profile/publish`, body, authHeaders());
      setUser((u) => ({ ...u, is_public: res.data.is_public }));
      fetchCommunity();
      showToast(res.data.is_public ? t("toast_profile_published") : t("toast_profile_removed"));
    } catch (e) {
      showToast(e.response?.data?.detail || "Erreur.", "error");
    }
  };

  const handleViewProfile = (profile) => {
    setPreviousReport(report);
    setReport({ ...profile, readOnly: true });
    setRanking([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackFromProfile = () => {
    setReport(previousReport);
    setPreviousReport(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    axios.get(`${API}/premium-status`)
      .then((res) => setPremiumStatus(res.data))
      .catch(() => {});
    fetchLeaderboard();
    fetchCommunity();
    if (!sessionStorage.getItem("rms_tracked")) {
      axios.post(`${API}/visit`).catch(() => {});
      sessionStorage.setItem("rms_tracked", "1");
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((res) => {
        setUser(res.data);
        setSearchesRemaining(res.data.searches_remaining ?? null);
        if (res.data.searches_limit) setSearchesLimit(res.data.searches_limit);
        fetchHistory(token);
        fetchTrophies(token);
        // Le theme est gratuit depuis le 2026-09-09 : le conditionner a l'acces
        // Premium le faisait disparaitre sous les yeux du joueur a l'expiration.
        if (res.data.preferences?.theme) {
          setTheme(res.data.preferences.theme);
        } else {
          localStorage.removeItem("rms_theme");
          setTheme({});
        }
      }).catch(() => {
        setToken(null);
        localStorage.removeItem("rms_token");
      });
    } else {
      localStorage.removeItem("rms_theme");
      setTheme({});
    }
    // Stocker le code de parrainage si présent dans l'URL
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      localStorage.setItem("rms_ref", refCode);
      setHasRefBonus(true);
      window.history.replaceState({}, "", "/");
    } else if (localStorage.getItem("rms_ref") && !user) {
      setHasRefBonus(true);
    }
    // Retour depuis vérification email
    if (params.get("email_verified") === "true") {
      window.history.replaceState({}, "", "/");
      setShowLogin(true);
      showToast(t("toast_verified"));
    }
    // Retour depuis Stripe
    if (params.get("subscribed") === "true") {
      window.history.replaceState({}, "", "/");
      if (token) {
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => setUser(res.data));
      }
    }
    // Retour depuis le récap hebdomadaire : « ton Pass 24h est terminé ». Le récap
    // est le seul canal de retour que le projet possède ; son lien doit tomber sur
    // la modale du Pass, pas sur l'accueil.
    if (params.get("pass") === "1") {
      window.history.replaceState({}, "", "/");
      setPendingPass(true);
    }
    // Réinitialisation mot de passe
    const resetToken = params.get("reset");
    if (resetToken) {
      window.history.replaceState({}, "", "/");
      setResetModal(resetToken);
    }
    // Chargement d'un rapport partagé
    const shareId = params.get("share");
    if (shareId) {
      axios.get(`${API}/share/${shareId}`)
        .then((res) => {
          setReport({ ...res.data, readOnly: true });
        })
        .catch(() => {});
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const authHeaders = () =>
    token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  // L'identifiant de navigateur ne part que sur /report : un en-tete non
  // standard impose un preflight CORS, inutile sur les appels courts.
  const reportHeaders = (jeton = token) => {
    const entetes = jeton ? { Authorization: `Bearer ${jeton}` } : {};
    const identifiant = visitorId();
    if (identifiant) entetes["X-Visitor-Id"] = identifiant;
    return { headers: entetes };
  };

  const [theme, setTheme] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rms_theme") || "{}"); } catch { return {}; }
  });

  const ACCENTS = {
    gold:   { gold: "#c89b3c", light: "#f0e6a6", dark: "#785a28", border: "rgba(200,155,60,0.2)" },
    blue:   { gold: "#0bc4e3", light: "#7de8f7", dark: "#0891a8", border: "rgba(11,196,227,0.25)" },
    purple: { gold: "#9b59b6", light: "#d7a8e8", dark: "#6c3483", border: "rgba(155,89,182,0.25)" },
    red:    { gold: "#e74c3c", light: "#f1948a", dark: "#922b21", border: "rgba(231,76,60,0.25)" },
  };

  React.useEffect(() => {
    const root = document.documentElement;
    const isLight = theme.mode === "light";
    const acc = ACCENTS[theme.accent] || ACCENTS.gold;
    document.body.setAttribute("data-mode", isLight ? "light" : "dark");

    // Couleurs de base
    if (isLight) {
      root.style.setProperty("--bg", "#eef2f7");
      root.style.setProperty("--panel", "#ffffff");
      root.style.setProperty("--panel-deep", "#e4eaf3");
      root.style.setProperty("--text", "#1a2332");
      root.style.setProperty("--muted", "#4a6080");
      root.style.setProperty("--glow", "0 4px 20px rgba(0,0,0,0.1)");
    } else {
      root.style.setProperty("--bg", "#010a13");
      root.style.setProperty("--panel", "#0d1b2a");
      root.style.setProperty("--panel-deep", "#071524");
      root.style.setProperty("--text", "#e8f1ff");
      root.style.setProperty("--muted", "#8fa6c2");
      root.style.setProperty("--glow", "0 20px 60px rgba(0,0,0,0.7)");
    }

    // Accent — met à jour toutes les variables dérivées
    root.style.setProperty("--gold", acc.gold);
    root.style.setProperty("--gold-light", acc.light);
    root.style.setProperty("--gold-dark", acc.dark);
    root.style.setProperty("--border", acc.border);
    root.style.setProperty("--glow-gold", `0 0 30px ${acc.border}`);
    root.style.setProperty("--accent-blue", acc.gold);
    // Teintes transparentes pour les effets CSS
    root.style.setProperty("--gold-a04", acc.gold + "0a");
    root.style.setProperty("--gold-a05", acc.gold + "0d");
    root.style.setProperty("--gold-a06", acc.gold + "0f");
    root.style.setProperty("--gold-a07", acc.gold + "12");
    root.style.setProperty("--gold-a08", acc.gold + "14");
    root.style.setProperty("--gold-a10", acc.gold + "1a");
    root.style.setProperty("--gold-a12", acc.gold + "1f");
    root.style.setProperty("--gold-a15", acc.gold + "26");
    root.style.setProperty("--gold-a20", acc.gold + "33");
    root.style.setProperty("--gold-a25", acc.gold + "40");
    root.style.setProperty("--gold-a30", acc.gold + "4d");
    root.style.setProperty("--gold-a35", acc.gold + "59");
    root.style.setProperty("--gold-a40", acc.gold + "66");
    root.style.setProperty("--gold-a50", acc.gold + "80");
    root.style.setProperty("--gold-a55", acc.gold + "8c");
    root.style.setProperty("--gold-a60", acc.gold + "99");
    root.style.setProperty("--gold-a75", acc.gold + "bf");

    // Fond — différences marquées entre chaque option
    const bg = theme.background || "default";
    document.body.style.backgroundSize = "";
    document.body.style.animation = "";
    if (bg === "minimal") {
      // Totalement plat, aucun gradient
      document.body.style.background = isLight ? "#eef2f7" : "#010a13";
    } else if (bg === "animated") {
      // Gradient coloré qui bouge avec la couleur d'accent
      const c1 = isLight ? "#dce8f5" : "#0d1e35";
      const c2 = isLight ? "#f0f4f8" : "#010a13";
      const c3 = acc.gold + "22"; // couleur d'accent translucide
      document.body.style.background = `linear-gradient(-45deg, ${c1}, ${c2}, ${c3}, ${c2}, ${c1})`;
      document.body.style.backgroundSize = "400% 400%";
      document.body.style.animation = "gradientShift 6s ease infinite";
    } else {
      // Défaut : gradient radial profond
      document.body.style.background = isLight
        ? "linear-gradient(135deg, #dce8f5 0%, #f0f4f8 50%, #e8eef5 100%)"
        : "radial-gradient(120% 140% at 20% 10%, #0d1e35 0%, #050e1a 50%, #010a13 100%)";
    }
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTheme = (patch) => {
    const next = { ...theme, ...patch };
    setTheme(next);
    if (isPremium) {
      axios.put(`${API}/auth/preferences`, { preferences: { theme: next } }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  };

  const handleLogin = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    setSearchesRemaining(userData.searches_remaining ?? null);
    if (userData.searches_limit) setSearchesLimit(userData.searches_limit);
    localStorage.setItem("rms_token", newToken);
    if (userData.preferences?.theme) {
      setTheme(userData.preferences.theme);
    } else {
      localStorage.removeItem("rms_theme");
      setTheme({});
    }
    setShowLogin(false);
    fetchHistory(newToken);
    fetchTrophies(newToken);
    if (userData.is_subscribed && report && lastSearch) {
      const { pseudo: p, region: r, mock } = lastSearch;
      const route = mock ? "report-mock" : "report";
      axios.get(
        `${API}/${route}/${r}/${encodeURIComponent(p)}`,
        reportHeaders(newToken)
      ).then((res) => setReport(res.data)).catch(() => {});
    }
  };

  const handleLogout = () => {
    setUpsellNotif(null);
    setToken(null);
    setUser(null);
    setHistory([]);
    setSearchesRemaining(null);
    setSearchesLimit(5);
    setReport(null);
    setRanking([]);
    setError("");
    setPseudo("");
    setLastSearch(null);
    localStorage.removeItem("rms_token");
  };

  const [resetModal, setResetModal] = useState(null); // token string
  const [resetForm, setResetForm] = useState({ next: "", confirm: "" });
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const [shareModal, setShareModal] = useState(null);
  const [roastLevel, setRoastLevel] = useState("piquant");
  const [trophies, setTrophies] = useState(null);
  const [roastLoading, setRoastLoading] = useState(false);
  const [roastVariant, setRoastVariant] = useState(0);
  const roastLevelRef = useRef("piquant");
  const roastVariantRef = useRef(0);
  const [shareLoading, setShareLoading] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState({ payment_active: false, waitlist_count: 0, stripe_url: null, total_reports: 0 });
  const [passModal, setPassModal] = useState(false);
  const [passEmail, setPassEmail] = useState("");
  const [passLoading, setPassLoading] = useState(false);
  const [passDone, setPassDone] = useState(null); // { etat, mail }
  const [passIntentId, setPassIntentId] = useState(null);

  const [pricingModal, setPricingModal] = useState(false);

  const [settingsModal, setSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState("premium");
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdError, setPwdError] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [cancelSubConfirm, setCancelSubConfirm] = useState(false);

  const [community, setCommunity] = useState([]);
  const [toast, setToast] = useState(null);
  const [legalModal, setLegalModal] = useState(null); // "privacy" | "cgu" | "mentions" | "contact" | null
  const [showFeedbackToast, setShowFeedbackToast] = useState(false);
  const feedbackTimerRef = useRef(null);
  const [adminOpen, setAdminOpen] = useState(false);
  // Messages non lus de la boite de reception : badge sur ◈, titre de l'onglet
  // et notification du bureau, tant qu'un admin est connecte.
  const [adminUnread, refreshAdminUnread] = useAdminUnread(token, !!user?.is_admin, () => setAdminOpen(true));

  useEffect(() => {
    const handler = (e) => setLegalModal(e.detail);
    window.addEventListener("openLegal", handler);
    return () => window.removeEventListener("openLegal", handler);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const tryShowUpsell = (forceMsg, score) => {
    const u = userRef.current;
    if (u?.is_subscribed) return;
    // Rien a vendre a qui lit une carte partagee : il n'a lance aucune analyse.
    if (readOnlyRef.current) return;
    const now = Date.now();
    if (!score && now - upsellCooldownRef.current < 3 * 60 * 1000) return;
    upsellCooldownRef.current = now;
    let msgFn;
    if (forceMsg) {
      msgFn = () => forceMsg;
    } else if (score !== undefined && score !== null && u) {
      if (score < 45) msgFn = (t) => t("upsell_score_low", score);
      else if (score <= 65) msgFn = (t) => t("upsell_score_mid", score);
      else msgFn = (t) => t("upsell_score_high", score);
    } else {
      const poolKey = u ? "upsell_free" : "upsell_anon";
      const idx = upsellIdxRef.current;
      upsellIdxRef.current++;
      msgFn = (t) => { const pool = t(poolKey); return pool[idx % pool.length]; };
    }
    setUpsellNotif({ msgFn, isAnon: !u });
  };
  tryShowUpsellRef.current = tryShowUpsell;

  // Déclenchement automatique : 45s, 5min, 10min après le chargement
  useEffect(() => {
    const timers = [45_000, 5 * 60_000, 10 * 60_000].map((d) =>
      setTimeout(() => tryShowUpsellRef.current?.(), d)
    );
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line

  // Sync ref pour accès stable dans les effets
  useEffect(() => { lastSearchRef.current = lastSearch; }, [lastSearch]);
  useEffect(() => { readOnlyRef.current = !!report?.readOnly; }, [report]);

  // Re-fetche le rapport quand la langue change
  useEffect(() => {
    const s = lastSearchRef.current;
    if (!s) return;
    const route = s.mock ? "report-mock" : "report";
    axios.get(`${API}/${route}/${s.region}/${encodeURIComponent(s.pseudo)}?lang=${lang}&roast_level=${roastLevelRef.current}&roast_variant=${roastVariantRef.current}`, reportHeaders())
      .then(res => setReport(prev => prev ? { ...res.data, region: s.region } : prev))
      .catch(() => {});
  }, [lang]); // eslint-disable-line

  // Déclenchement quand il reste peu de recherches
  useEffect(() => {
    // `!searchesRemaining` attrapait aussi 0 : le message « limite atteinte »
    // ne s'affichait donc jamais.
    if (searchesRemaining === null || searchesRemaining > 2 || isPremium) return;
    if (report?.readOnly) return;
    const n = searchesRemaining;
    const anonyme = !user;
    const msgFn = n === 0
      ? (t) => t(anonyme ? "upsell_limit_0_anon" : "upsell_limit_0")
      : (t) => t(anonyme ? "upsell_limit_n_anon" : "upsell_limit_n", n);
    // `isAnon` decide de la destination du clic : l'inscription pour un
    // visiteur, l'abonnement pour un compte gratuit.
    setUpsellNotif({ msgFn, isAnon: anonyme });
    upsellCooldownRef.current = Date.now();
  }, [searchesRemaining]); // eslint-disable-line

  const handleSubscribe = async () => {
    if (premiumStatus.payment_active && premiumStatus.stripe_url) {
      window.open(premiumStatus.stripe_url, "_blank");
      return;
    }
    // La modale s'ouvre tout de suite, l'intention part en parallele : c'est le
    // clic a 1,99 EUR qu'on mesure, pas le remplissage du formulaire. Attendre
    // le reseau avant d'afficher quoi que ce soit ferait payer la mesure a
    // l'utilisateur.
    console.log("intent_achat_pass_24h");
    setPassEmail(user?.email || "");
    setPassDone(null);
    setPassIntentId(null);
    setPassModal(true);
    try {
      const res = await axios.post(
        `${API}/pass-intent`,
        { lang },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      setPassIntentId(res.data?.intent_id ?? null);
    } catch (e) {
      // Une intention perdue ne doit jamais priver quelqu'un de son pass.
    }
  };

  // Le lien du recap ouvre la modale du Pass des que la page est prete.
  useEffect(() => {
    if (!pendingPass) return;
    setPendingPass(false);
    handleSubscribe();
  }, [pendingPass]); // eslint-disable-line

  const handlePassUnlock = async () => {
    setPassLoading(true);
    try {
      if (token && user) {
        // Deja connecte : rien a demander, on accorde et on relit le compte.
        const res = await axios.post(
          `${API}/auth/grant-pass`,
          { intent_id: passIntentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const moi = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(moi.data);
        setPassDone({ etat: res.data.etat, mail: false });
      } else {
        if (!passEmail.trim()) {
          setPassLoading(false);
          return;
        }
        const res = await axios.post(`${API}/pass-signup`, {
          email: passEmail.trim(),
          lang,
          intent_id: passIntentId,
          ref: localStorage.getItem("rms_ref") || null,
        });
        if (res.data.existing_account) {
          // Aucun jeton n'est delivre pour une adresse deja connue : la repondre
          // suffirait sinon a prendre le controle du compte d'un tiers.
          setPassDone({ etat: "compte_existant", mail: false });
        } else {
          // On pose le jeton et on laisse l'effet sur `token` relire /auth/me :
          // la reponse de /pass-signup ne porte qu'un utilisateur minimal.
          localStorage.setItem("rms_token", res.data.token);
          setToken(res.data.token);
          setPassDone({ etat: "accorde", mail: true });
        }
      }
      axios.get(`${API}/premium-status`)
        .then((r) => setPremiumStatus(r.data))
        .catch(() => {});
    } catch (e) {
      showToast(t("pass_error"), "error");
    }
    setPassLoading(false);
  };

  const handleReset = () => {
    setReport(null);
    setRanking([]);
    setError("");
    setIsDemoMode(false);
    window.history.replaceState({}, "", "/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShare = async (reportData) => {
    // Le partage est ouvert à tous : c'est notre principal canal d'acquisition.
    // Seule la démo reste bloquée, son rapport étant factice.
    if (isDemoMode) return handleSubscribe();
    setShareLoading(true);
    try {
      // Jeton de propriete de la carte : sans lui, le backend refuse de mettre a
      // jour un partage existant, ce qui empeche un tiers d'ecraser la carte d'un
      // joueur. Conserve par navigateur, cle par pseudo et region.
      const shareKey = `rms_share_${(reportData.player || "").toLowerCase()}_${(reportData.region || region || "").toLowerCase()}`;
      const res = await axios.post(`${API}/share`, {
        player: reportData.player,
        region: reportData.region || region,
        score: reportData.score,
        badge: reportData.badge,
        phrase: reportData.phrase,
        rank: reportData.rank,
        level: reportData.level,
        stats: reportData.stats,
        extra: reportData.extra,
        mapPerformance: reportData.mapPerformance,
        owner_token: localStorage.getItem(shareKey) || undefined,
      });
      if (res.data?.owner_token) {
        try { localStorage.setItem(shareKey, res.data.owner_token); } catch { /* quota plein : le partage marche quand meme */ }
      }
      setShareModal({ url: res.data.url, id: res.data.id, report: reportData });
    } catch (e) {
      const text = `Roast My Stats — ${reportData.player}\nScore: ${reportData.score}/100\nBadge: ${reportData.badge}\n"${reportData.phrase}"\n\nroast-my-stats.com`;
      navigator.clipboard.writeText(text).then(() => {
        showToast("Rapport copié dans le presse-papier !");
      }).catch(() => {
        showToast("Impossible de copier. Partage manuellement !");
      });
    }
    setShareLoading(false);
  };

  // Re-roast : les 3 punchlines sont generees en un seul appel IA et mises en cache
  // cote backend, donc changer de variante ne recoute rien.
  const refetchRoast = async (level, variant) => {
    const s = lastSearchRef.current;
    if (!s || s.mock) return;
    roastLevelRef.current = level;
    roastVariantRef.current = variant;
    setRoastLoading(true);
    try {
      const res = await axios.get(
        `${API}/report/${s.region}/${encodeURIComponent(s.pseudo)}?lang=${lang}&roast_level=${level}&roast_variant=${variant}`,
        authHeaders()
      );
      setReport((prev) => (prev ? { ...res.data, region: s.region } : prev));
    } catch (e) {
      showToast(t("roast_error"), "error");
    }
    setRoastLoading(false);
  };

  const handleReroast = () => {
    if (!user) return setShowLogin(true);
    const next = (roastVariant + 1) % 3;
    setRoastVariant(next);
    refetchRoast(roastLevel, next);
  };

  const handleRoastLevel = (level) => {
    if (!user) return setShowLogin(true);
    setRoastLevel(level);
    setRoastVariant(0);
    refetchRoast(level, 0);
  };

  const handleCloseShareModal = () => setShareModal(null);


  const handleChallenge = (playerPseudo) => {
    const basePseudo = playerPseudo.split(",")[0].trim();
    setPseudo(basePseudo + ", ");
    window.scrollTo({ top: 0, behavior: "smooth" });
    showToast(t("toast_challenge"));
    setTimeout(() => pseudoInputRef.current?.focus(), 400);
  };

  const handleResetPassword = async () => {
    setResetError("");
    const { next, confirm } = resetForm;
    if (next !== confirm) return setResetError("Les mots de passe ne correspondent pas.");
    if (next.length < 8) return setResetError("Au moins 8 caractères.");
    if (!/[A-Z]/.test(next)) return setResetError("Au moins une majuscule.");
    if (!/[0-9]/.test(next)) return setResetError("Au moins un chiffre.");
    if (!/[^A-Za-z0-9]/.test(next)) return setResetError("Au moins un caractère spécial.");
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetModal, new_password: next });
      setResetDone(true);
    } catch (e) {
      setResetError(e.response?.data?.detail || "Lien invalide ou expiré.");
    }
    setResetLoading(false);
  };

  const handleChangePassword = async () => {
    setPwdError("");
    const { current, next, confirm } = pwdForm;
    if (next !== confirm) return setPwdError("Les mots de passe ne correspondent pas.");
    if (next.length < 8) return setPwdError("Au moins 8 caractères.");
    if (!/[A-Z]/.test(next)) return setPwdError("Au moins une majuscule.");
    if (!/[0-9]/.test(next)) return setPwdError("Au moins un chiffre.");
    if (!/[^A-Za-z0-9]/.test(next)) return setPwdError("Au moins un caractère spécial.");
    setPwdLoading(true);
    try {
      const res = await axios.put(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        authHeaders()
      );
      // Changer de mot de passe invalide les jetons deja emis, celui-ci compris :
      // le backend en renvoie un neuf, sans quoi la session tomberait en 401.
      if (res.data?.token) {
        localStorage.setItem("rms_token", res.data.token);
        setToken(res.data.token);
      }
      setPwdForm({ current: "", next: "", confirm: "" });
      showToast(t("toast_password_changed"));
    } catch (e) {
      setPwdError(e.response?.data?.detail || "Erreur.");
    }
    setPwdLoading(false);
  };

  const handleCancelSubscription = async () => {
    try {
      await axios.post(`${API}/auth/cancel-subscription`, {}, authHeaders());
      setUser((u) => ({ ...u, is_subscribed: false }));
      showToast(t("toast_subscription_cancelled"));
    } catch (e) {
      showToast(e.response?.data?.detail || "Erreur.", "error");
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/auth/account`, authHeaders());
      handleLogout();
      setSettingsModal(false);
      showToast(t("toast_account_deleted"));
    } catch (e) {
      showToast(t("toast_error_delete"), "error");
    }
  };

  const generateDemo = async () => {
    const demoPseudos = ["ShadowBlade#EUW", "IronWolf#EUW", "StarFire#EUW", "NightHawk#EUW", "StormRider#EUW"];
    const pick = demoPseudos[Math.floor(Math.random() * demoPseudos.length)];
    setError("");
    setLoadingLabel(t("loading_demo"));
    setLoading(true);
    setLastSearch({ pseudo: pick, region: "euw", mock: true });
    try {
      const res = await axios.get(`${API}/report-mock/euw/${encodeURIComponent(pick)}?lang=${lang}`, authHeaders());
      setReport({ ...res.data, region: "euw" });
      setRanking([]);
      setIsDemoMode(true);
    } catch {
      setError("Erreur lors de la démo.");
    }
    setLoading(false);
    setLoadingLabel(null);
  };

  const generateDemoSquad = async () => {
    setError("");
    setLoadingLabel(t("loading_squad"));
    setLoading(true);
    const demoPseudos = ["ShadowBlade#EUW", "IronWolf#EUW", "StarFire#EUW", "NightHawk#EUW"];
    try {
      const results = await Promise.all(
        demoPseudos.map((p) =>
          axios.get(`${API}/report-mock/euw/${encodeURIComponent(p)}?lang=${lang}`, authHeaders())
            .then((r) => ({ ...r.data, player: p, score: Math.floor(Math.random() * 40) + 55 }))
        )
      );
      const sorted = results.sort((a, b) => b.score - a.score);
      setRanking(sorted);
      setReport(null);
      setIsDemoMode(true);
    } catch (e) {
      setError("Erreur lors de la démo squad.");
    }
    setLoading(false);
    setLoadingLabel(null);
  };

  const generateReport = async (mock = false) => {
    if (!pseudo.trim()) return showToast("Entre un pseudo !", "error");
    setShowSuggestions(false);
    setError("");
    setIsDemoMode(false);
    setLoading(true);

    // Feedback toast : apparaît après 2.5s si pas encore donné d'avis
    clearTimeout(feedbackTimerRef.current);
    if (!localStorage.getItem("rms_feedback_given")) {
      feedbackTimerRef.current = setTimeout(() => setShowFeedbackToast(true), 2500);
    }

    const searchPseudo = pseudo.trim();
    setLastSearch({ pseudo: searchPseudo, region, mock });
    try {
      const route = mock ? "report-mock" : "report";
      const res = await axios.get(
        `${API}/${route}/${region}/${encodeURIComponent(searchPseudo)}?lang=${lang}&roast_level=${roastLevel}&roast_variant=${roastVariant}`,
        reportHeaders()
      );
      if (user) saveToSearchHistory(searchPseudo, region);
      setPseudo("");
      setReport({ ...res.data, region });
      setLbSubmitInput(res.data.player || "");
      setLbSubmitDone(false);
      setRanking([]);
      tryShowUpsell(null, res.data.score);
      if (res.data.searches_remaining !== undefined) setSearchesRemaining(res.data.searches_remaining);
      if (res.data.searches_limit) setSearchesLimit(res.data.searches_limit);
      if (token) { fetchHistory(token); fetchTrophies(token); }
      fetchLeaderboard();
    } catch (e) {
      clearTimeout(feedbackTimerRef.current);
      const status = e.response?.status;
      if (status === 429) {
        setError(e.response.data.detail);
        if (!isPremium) setSearchesRemaining(0);
        // Sans compte, le message invite a s'inscrire : autant ouvrir le
        // formulaire. Le refus etait jusqu'ici un simple texte rouge, au moment
        // precis ou l'inscription a le plus de sens.
        if (!user) { setLoginTab("register"); setShowLogin(true); }
      } else {
        setError(t("error_not_found"));
      }
    }
    setLoading(false);
  };

  const generateSquad = async (overridePseudo) => {
    if (!user) return setShowLogin(true);
    if (!isPremium) return handleSubscribe();
    const source = typeof overridePseudo === "string" ? overridePseudo : pseudo;
    const pseudos = source.split(",").map((p) => p.trim()).filter((p) => p !== "");
    if (pseudos.length === 0) return showToast("Entre au moins un pseudo !", "error");
    setShowSuggestions(false);
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/report-multiple/${region}?lang=${lang}`,
        pseudos,
        authHeaders()
      );
      if (user) pseudos.forEach((p) => saveToSearchHistory(p, region));
      setRanking(res.data);
      setReport(null);
      const failed = res.data[0]?._failed;
      if (failed?.length) showToast(`Joueurs introuvables ignorés : ${failed.join(", ")}`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) {
        setError(t("error_squad_premium"));
      } else {
        setError(t("error_squad"));
      }
    }
    setLoading(false);
  };

  const isReadOnly = report?.readOnly || false;

  if (showLeaderboardPage) {
    return (
      <div className="page-bg">
        <div className="app-shell">
          <div className="hero-panel">
            <div>
              <h1 className="brand-title">Roast My Stats</h1>
              <p className="brand-sub">{t("leaderboard_page_title")}</p>
            </div>
            <button className="cta cta-ghost" onClick={() => {
              window.history.replaceState({}, "", "/");
              setShowLeaderboardPage(false);
            }}>
              {t("back")}
            </button>
          </div>

          <div className="card framed" style={{ marginTop: '1.5rem', padding: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.125rem' }}>{t("leaderboard_best")}</h2>
              <p style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: '0.25rem' }}>{t("leaderboard_updated")}</p>
            </div>

            {leaderboard.length === 0 ? (
              <p style={{ color: "var(--muted)", fontSize: '0.8125rem' }}>{t("leaderboard_empty")}</p>
            ) : (
              <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {leaderboard.map((entry) => (
                  <li key={entry.rank} style={{
                    display: "flex", alignItems: "center", gap: '0.75rem',
                    padding: "0.625rem 0", borderBottom: "1px solid rgba(255,255,255,0.06)"
                  }}>
                    <span style={{ width: '1.75rem', fontWeight: 700, color: entry.rank <= 3 ? "var(--gold)" : "var(--muted)", fontSize: '0.875rem' }}>
                      #{entry.rank}
                    </span>
                    <span style={{
                      width: '2.25rem', height: '2.25rem', borderRadius: "50%",
                      background: "rgba(255,255,255,0.08)", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: '0.9375rem', flexShrink: 0
                    }}>
                      {entry.player.charAt(0).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, fontWeight: 500 }}>{entry.player}</span>
                    {entry.badge && <span style={{ fontSize: '0.75rem', color: "var(--muted)" }}>{entry.badge}</span>}
                    <span style={{ fontWeight: 700, color: "var(--gold)" }}>{entry.score}/100</span>
                  </li>
                ))}
              </ol>
            )}

            <div style={{ marginTop: '1.5rem', textAlign: "center" }}>
              <button className="cta cta-primary" onClick={() => {
                window.history.replaceState({}, "", "/");
                setShowLeaderboardPage(false);
              }}>
                {t("leaderboard_analyze")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-bg">
      <div className="app-shell">
        {phasePH() && phFerme !== phasePH() && (
          <div style={{ background: "linear-gradient(90deg, #ff615422, #ff615444)", border: "1px solid #FF6154", borderRadius: '0.625rem', padding: "0.75rem 1.125rem", marginBottom: '0.75rem', display: "flex", alignItems: "center", gap: '0.5rem' }}>
            <a
              href={PH_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: '0.5rem', fontSize: '0.875rem', color: "#FF8E85", fontWeight: 600, textDecoration: "none" }}
            >
              <ArrowUp size={16} />{t(phasePH() === "live" ? "ph_banner_live" : "ph_banner_soon")}
            </a>
            <button
              onClick={() => { const ph = phasePH(); setPhFerme(ph); try { localStorage.setItem("rms_ph_banner", ph); } catch {} }}
              aria-label="Fermer"
              style={{ background: "none", border: "none", color: "#FF8E85", cursor: "pointer", padding: 0, flexShrink: 0, lineHeight: 1, display: "flex", opacity: 0.7 }}
            >
              <X size={14} />
            </button>
          </div>
        )}
        {hasRefBonus && !user && (
          <div style={{ background: "linear-gradient(90deg, #c89b3c22, #c89b3c44)", border: "1px solid var(--gold)", borderRadius: '0.625rem', padding: "0.75rem 1.125rem", marginBottom: '0.75rem', textAlign: "center", fontSize: '0.875rem', color: "var(--gold)", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: '0.5rem' }} onClick={() => setShowLogin(true)}>
            <Gift size={16} />{t("referral_gift_banner")}
          </div>
        )}
        {!isReadOnly && <div className="hero-panel">
          <div>
            <h1 className="brand-title">Roast My Stats</h1>
            <p className="brand-sub">{t("brand_sub")}</p>
            {premiumStatus.total_reports > 0 && (
              <p style={{ fontSize: '0.8125rem', color: "var(--gold)", marginTop: '0.375rem', fontWeight: 600, display: "flex", alignItems: "center", gap: '0.3125rem' }}>
                <Users size={13} />{t("total_reports", premiumStatus.total_reports)}
              </p>
            )}
            <div style={{ display: "flex", gap: '0.5rem', flexWrap: "wrap", marginTop: '0.5rem' }}>
              {leaderboard.length > 0 && (
                <button
                  className="cta cta-ghost"
                  style={{ fontSize: '0.75rem', padding: "0.375rem 0.875rem" }}
                  onClick={() => { window.history.pushState({}, "", "/leaderboard"); setShowLeaderboardPage(true); }}
                >
                  {t("see_leaderboard")}
                </button>
              )}
              <button
                className="cta cta-ghost"
                style={{ fontSize: '0.75rem', padding: "0.375rem 0.875rem" }}
                onClick={() => setPricingModal(true)}
              >
                {t("see_offers")}
              </button>
            </div>
          </div>
          <div style={{ display: "flex", gap: '0.625rem', alignItems: "center" }}>
            {user ? (
              <>
                {!isPremium && (
                  <button className="cta cta-primary" onClick={handleSubscribe}>
                    {t("go_premium")}
                  </button>
                )}

                {user.is_subscribed && <div className="pill" style={{ borderColor: "var(--gold-a40)", color: "var(--gold)" }}>Premium ✓</div>}
                {!user.is_subscribed && user.pass_active && (
                  <div className="pill" style={{ borderColor: "var(--gold-a40)", color: "var(--gold)" }}>
                    {t("pass_badge", user.pass_hours_remaining)}
                  </div>
                )}
                <button className="cta cta-ghost hero-logout-btn" onClick={handleLogout}>
                  {t("logout", user.firstname || user.email.split("@")[0])}
                </button>
                <button className="cta cta-ghost settings-btn" onClick={() => { setSettingsTab("account"); setSettingsModal(true); }} title="Paramètres">
                  ⚙
                </button>
                {user.is_admin && (
                  <button className="cta cta-ghost settings-btn admin-open-btn" onClick={() => setAdminOpen(true)}
                    title={adminUnread > 0 ? `Admin — ${adminUnread} message${adminUnread > 1 ? "s" : ""} non lu${adminUnread > 1 ? "s" : ""}` : "Admin"}>
                    ◈
                    {adminUnread > 0 && <span className="admin-unread-dot">{adminUnread > 99 ? "99+" : adminUnread}</span>}
                  </button>
                )}
              </>
            ) : (
              <>
                <button className="cta cta-primary" onClick={handleSubscribe}>
                  {t("go_premium")}
                </button>
                <button className="cta cta-ghost" onClick={() => setShowLogin(true)}>
                  {t("lm_tab_login")}
                </button>
              </>
            )}
            <button
              className="cta cta-ghost"
              style={{ fontSize: '0.6875rem', padding: "0.25rem 0.625rem", letterSpacing: 1, fontWeight: 700 }}
              onClick={() => setLanguage(lang === "fr" ? "en" : "fr")}
            >
              {t("lang_toggle")}
            </button>
          </div>
        </div>}

        {!isReadOnly && <div className="input-row">
          <div className="input-search-row">
          <select
            className="rm-input"
            style={{ flex: "0 0 auto", width: "auto" }}
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>{window.innerWidth < 600 ? r.value.toUpperCase() : r.label}</option>
            ))}
          </select>
          <div style={{ position: "relative", flex: 1 }}>
            <input
              ref={pseudoInputRef}
              className="rm-input"
              style={{ width: "100%" }}
              value={pseudo}
              onChange={(e) => setPseudo(e.target.value)}
              placeholder={t("search_placeholder")}
              onKeyDown={(e) => e.key === "Enter" && generateReport()}
              onFocus={() => setShowSuggestions(true)}
              autoComplete="off"
            />
            {user && showSuggestions && searchHistory.length > 0 && (() => {
              const parts = pseudo.split(",");
              const currentPart = parts[parts.length - 1].trim();
              const alreadyAdded = parts.slice(0, -1).map((p) => p.trim().toLowerCase());
              const hasComma = pseudo.includes(",");
              const suggestions = searchHistory.filter((item) => {
                if (alreadyAdded.includes(item.pseudo.toLowerCase())) return false;
                if (hasComma && currentPart === "") return true;
                return item.pseudo.toLowerCase().includes(currentPart.toLowerCase());
              });
              if (!suggestions.length) return null;
              return (
              <ul ref={suggestionsRef} className="search-suggestions">
                {suggestions.map((item) => (
                    <li key={item.pseudo} className="suggestion-item">
                      <span
                        className="suggestion-text"
                        onMouseDown={() => {
                          const before = parts.slice(0, -1).map((x) => x.trim()).filter(Boolean);
                          setPseudo(before.length ? before.join(", ") + ", " + item.pseudo : item.pseudo);
                          if (user.is_subscribed) setRegion(item.region);
                          setShowSuggestions(false);
                          pseudoInputRef.current?.focus();
                        }}
                      >
                        {item.pseudo}
                        {user.is_subscribed && (
                          <span style={{ fontSize: '0.6875rem', color: "var(--muted)", marginLeft: '0.375rem' }}>
                            {item.region.toUpperCase()}
                          </span>
                        )}
                      </span>
                      <button
                        className="suggestion-delete"
                        onMouseDown={(e) => { e.stopPropagation(); removeFromSearchHistory(item.pseudo); }}
                        title="Supprimer"
                      >
                        ×
                      </button>
                    </li>
                  ))}
              </ul>
              );
            })()}
          </div>
          </div>
          <div className="input-actions">
            <button className="cta cta-primary" onClick={() => generateReport(false)}>
              {t("btn_report")}
            </button>
            <button className="cta cta-ghost" onClick={generateSquad}>
              {t("btn_squad")}
            </button>
            <button
              className="cta cta-ghost"
              style={{ borderColor: "var(--gold-a40)", color: "var(--gold)" }}
              onClick={generateDemo}
            >
              {t("btn_demo")}
            </button>
            <button
              className="cta cta-ghost"
              style={{ borderColor: "var(--gold-a40)", color: "var(--gold)" }}
              onClick={generateDemoSquad}
            >
              {t("btn_demo_squad")}
            </button>
          </div>
        </div>}

        {!isReadOnly && !isDemoMode && !isPremium && searchesRemaining !== null && (
          <div className="searches-bar">
            <span className="searches-label">
              {searchesRemaining === 0
                ? t("limit_reached")
                : t("searches_remaining", searchesRemaining)}
            </span>
            <div className="searches-track">
              <div
                className="searches-fill"
                style={{ width: `${(searchesRemaining / (searchesLimit || 5)) * 100}%` }}
              />
            </div>
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className={`spinner${user?.is_subscribed ? " spinner-premium" : ""}`} />
            <span className="spinner-label">
              {loadingLabel || (user?.is_subscribed
                ? t("loading_premium")
                : user
                ? t("loading_free")
                : t("loading_anon"))}
            </span>
          </div>
        )}
        {error && (
          <div className="error-msg">
            {error}
            {(error.includes("Limite") || error.includes("limit") || error.includes("Premium")) && (
              <button
                className="cta cta-primary"
                onClick={handleSubscribe}
                style={{ marginLeft: '0.75rem', fontSize: '0.8125rem', padding: "0.375rem 0.875rem" }}
              >
                {t("go_premium_btn")}
              </button>
            )}
            {(error.includes("introuvable") || error.includes("not found") || error.includes("Not found")) && (
              <button
                className="cta cta-ghost"
                onClick={generateDemo}
                style={{ marginLeft: '0.5rem', fontSize: '0.8125rem', padding: "0.375rem 0.875rem" }}
              >
                {t("error_try_demo")}
              </button>
            )}
          </div>
        )}

        {report && (
          <div className="section-gap">
            <DashboardVisual
              report={report}
              onSubscribe={handleSubscribe}
              onCreateAccount={() => { setLoginTab("register"); setShowLogin(true); }}
              onShare={handleShare}
              onReroast={handleReroast}
              trophies={trophies}
              newTrophies={report?.newTrophies || []}
              onRoastLevel={handleRoastLevel}
              roastLevel={roastLevel}
              roastLoading={roastLoading}
              onChallenge={handleChallenge}
              onReset={handleReset}
              isDemoMode={isDemoMode}
              currentUser={user}
              // La promesse est rendue a l'appelant : l'avalement silencieux qui
              // tenait ici affichait « merci » meme quand le vote n'etait jamais
              // enregistre (quota horaire, reseau), et le compteur admin restait
              // vide sans que personne puisse s'en apercevoir.
              onAccuracyFeedback={(accurate) =>
                axios.post(`${API}/report-accuracy`, {
                  accurate,
                  player: report.player,
                  score: report.score,
                })
              }
              history={isReadOnly ? [] : isDemoMode ? [{created_at: new Date().toISOString(), player: report.player, score: report.score, badge: report.badge}] : history}
              squadRanking={isReadOnly ? [] : ranking}
              leaderboard={leaderboard}
              community={community}
              isPublic={user?.is_public || false}
              onTogglePublish={handleTogglePublish}
              onViewProfile={handleViewProfile}
              shareLoading={shareLoading}
              readOnly={isReadOnly}
              onBack={isReadOnly && previousReport ? handleBackFromProfile : null}
              t={t}
              lang={lang}
            />
            {!isReadOnly && user && (
              <div style={{ textAlign: "center", marginTop: '0.75rem', display: "flex", flexDirection: "column", alignItems: "center", gap: '0.375rem' }}>
                <span style={{ fontSize: '0.75rem', color: "var(--muted)" }}>{t("lb_submit_label")}</span>
                <div style={{ display: "flex", gap: '0.375rem' }}>
                  <input
                    className="rm-input"
                    style={{ fontSize: '0.8125rem', padding: "0.375rem 0.625rem", width: '11.25rem' }}
                    placeholder={t("lb_submit_placeholder")}
                    value={lbSubmitInput}
                    onChange={(ev) => { setLbSubmitInput(ev.target.value); setLbSubmitDone(false); }}
                    onKeyDown={(ev) => {
                      if (ev.key !== "Enter" || lbSubmitDone || !lbSubmitInput.trim()) return;
                      const player = lbSubmitInput.trim();
                      if (leaderboard.some(entry => entry.player === player)) { setLbSubmitDone(true); return; }
                      axios.post(`${API}/leaderboard/submit`, { player }, { headers: { Authorization: `Bearer ${token}` } })
                        .then(() => { fetchLeaderboard(); setLbSubmitDone(true); })
                        .catch(() => {});
                    }}
                  />
                  <button
                    className="cta cta-ghost"
                    style={{ fontSize: '0.75rem', padding: "0.375rem 0.75rem" }}
                    disabled={lbSubmitDone || !lbSubmitInput.trim()}
                    onClick={async () => {
                      const player = lbSubmitInput.trim();
                      if (!player) return;
                      if (leaderboard.some(entry => entry.player === player)) { setLbSubmitDone(true); return; }
                      await axios.post(`${API}/leaderboard/submit`, { player }, { headers: { Authorization: `Bearer ${token}` } });
                      fetchLeaderboard();
                      setLbSubmitDone(true);
                    }}
                  >
                    {lbSubmitDone ? t("lb_submit_done") : t("lb_submit_btn")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {ranking.length > 0 && (
          <div className="section-gap">
            <div className="squad-header">
              <h2 className="squad-title">{t("squad_title")}</h2>
              <button className="cta cta-ghost" onClick={handleReset} style={{ fontSize: '0.8125rem' }}>
                {t("new_report")}
              </button>
            </div>
            {ranking.map((r, i) => (
              <DashboardVisual
                key={i}
                report={r}
                onSubscribe={handleSubscribe}
                onCreateAccount={() => { setLoginTab("register"); setShowLogin(true); }}
                onShare={handleShare}
                onChallenge={handleChallenge}
                onReset={handleReset}
                history={isDemoMode ? [{created_at: new Date().toISOString(), player: r.player, score: r.score, badge: r.badge}] : history}
                squadRanking={ranking}
                currentUser={user}
                t={t}
                lang={lang}
              />
            ))}
          </div>
        )}
      </div>

      {pricingModal && (
        <div className="modal-overlay" onClick={() => setPricingModal(false)}>
          <div className="modal-box" style={{ maxWidth: '42.5rem', width: "92vw" }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setPricingModal(false)}><X size={16} /></button>
            <h2 className="modal-title" style={{ marginBottom: '0.25rem' }}>{t("choose_offer")}</h2>
            <p style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: '1.5rem', textAlign: "center" }}>{t("choose_offer_sub")}</p>
            <div className="plans-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: '0.75rem' }}>
              {[
                {
                  label: t("plan_no_account"),
                  price: t("plan_free_price"),
                  color: "var(--muted)",
                  quota: t("quota_anon"),
                  features: [
                    { text: t("feature_history"), ok: false },
                    { text: t("feature_suggestions"), ok: false },
                    { text: t("feature_roast_levels"), ok: false },
                    { text: t("feature_share"), ok: true },
                    { text: t("feature_themes"), ok: false },
                    { text: t("feature_squad"), ok: false },
                    { text: t("feature_community"), ok: false },
                    { text: t("feature_referrals_none"), ok: false },
                  ],
                  cta: null,
                },
                {
                  // ATTENTION : cette grille est dupliquee dans l'onglet Premium des
                  // reglages (« Comparer les offres »). Toute modification ici doit y
                  // etre reportee — c'est ce duplicata qui a laisse passer, le
                  // 2026-09-09, une grille encore calee sur l'ancienne frontiere.
                  label: t("plan_free"),
                  price: t("plan_free_account_price"),
                  color: "var(--text)",
                  quota: t("quota_free"),
                  features: [
                    { text: t("feature_history"), ok: true },
                    { text: t("feature_suggestions"), ok: true },
                    { text: t("feature_roast_levels"), ok: true },
                    { text: t("feature_share"), ok: true },
                    { text: t("feature_themes"), ok: true },
                    { text: t("feature_squad"), ok: false },
                    { text: t("feature_community"), ok: true },
                    { text: t("feature_referrals_link"), ok: true },
                  ],
                  cta: { label: t("create_account"), action: () => { setPricingModal(false); setShowLogin(true); } },
                },
                {
                  label: t("plan_premium"),
                  price: t("plan_premium_price"),
                  color: "var(--gold)",
                  highlight: true,
                  quota: t("quota_premium"),
                  features: [
                    { text: t("feature_ai_coach"), ok: true },
                    { text: t("feature_history"), ok: true },
                    { text: t("feature_suggestions"), ok: true },
                    { text: t("feature_roast_levels"), ok: true },
                    { text: t("feature_share"), ok: true },
                    { text: t("feature_themes"), ok: true },
                    { text: t("feature_squad"), ok: true },
                    { text: t("feature_community"), ok: true },
                    { text: t("feature_referrals_link"), ok: true },
                  ],
                  cta: { label: t("go_premium_cta"), action: () => { setPricingModal(false); handleSubscribe(); } },
                },
              ].map((plan) => (
                <div key={plan.label} style={{
                  borderRadius: '0.875rem',
                  border: plan.highlight ? "2px solid var(--gold)" : "1px solid var(--border)",
                  background: plan.highlight ? "var(--gold-a06)" : "var(--panel-deep)",
                  padding: "1.25rem 1rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}>
                  <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: plan.color, marginBottom: '0.25rem' }}>{plan.label}</div>
                  <div style={{ fontSize: '1.375rem', fontWeight: 800, color: plan.color, marginBottom: '1rem' }}>{plan.price}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: '0.5rem', flex: 1, marginBottom: '1.25rem' }}>
                    {[{ text: plan.quota, ok: true }, ...plan.features].map((f) => (
                      <div key={f.text} style={{ display: "flex", gap: '0.5rem', alignItems: "flex-start", fontSize: '0.75rem', color: f.ok ? "var(--text)" : "var(--muted)", opacity: f.ok ? 1 : 0.45 }}>
                        <span style={{ color: f.ok ? "var(--gold)" : "var(--muted)", flexShrink: 0, marginTop: 1 }}>{f.ok ? "✓" : "✗"}</span>
                        {f.text}
                      </div>
                    ))}
                  </div>
                  {plan.cta && (
                    <button
                      className={`cta ${plan.highlight ? "cta-primary" : "cta-ghost"} wide`}
                      style={{ fontSize: '0.75rem' }}
                      onClick={plan.cta.action}
                    >
                      {plan.cta.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Les classes `waitlist-*` sont conservees telles quelles : elles portent
          le style de cette modale dans DashboardVisual.css. Les renommer
          demanderait de toucher la feuille de style sans rien changer a l'ecran. */}
      {passModal && (
        <div className="modal-overlay" onClick={() => setPassModal(false)}>
          <div className="modal-box waitlist-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setPassModal(false)}><X size={16} /></button>
            {!passDone ? (
              <>
                <div className="waitlist-icon"><Crown size={32} style={{ color: "var(--gold)" }} /></div>
                <h2 className="modal-title">{t("going_premium")}</h2>
                <p className="waitlist-sub">
                  {user ? t("pass_body_user") : t("pass_body_anon")}
                </p>

                {/* Un compte connecte n'a pas d'adresse a redonner. */}
                {!user && (
                  <input
                    className="rm-input waitlist-input"
                    type="email"
                    placeholder={t("email_placeholder")}
                    value={passEmail}
                    onChange={(e) => setPassEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handlePassUnlock()}
                    autoFocus
                  />
                )}
                <button
                  className="cta cta-primary wide"
                  onClick={handlePassUnlock}
                  disabled={passLoading || (!user && !passEmail.trim())}
                >
                  {passLoading ? "..." : user ? t("pass_cta_user") : t("pass_cta_anon")}
                </button>
              </>
            ) : (
              <>
                <div className="waitlist-icon">
                  <CheckCircle size={40} style={{ color: "var(--gold)" }} />
                </div>
                <h2 className="modal-title">
                  {passDone.etat === "compte_existant"
                    ? t("going_premium")
                    : passDone.etat === "abonnement_actif" || passDone.etat === "deja_actif"
                      ? t("pass_already_title")
                      : t("pass_success_title")}
                </h2>
                <p className="waitlist-sub">
                  {passDone.etat === "compte_existant"
                    ? t("pass_existing_account")
                    : passDone.etat === "abonnement_actif" || passDone.etat === "deja_actif"
                      ? t("pass_already_body")
                      : t("pass_success_body")}
                  {passDone.mail && (
                    <><br /><br />{t("pass_success_mail")}</>
                  )}
                </p>
                <button className="cta cta-ghost wide" onClick={() => setPassModal(false)}>{t("close")}</button>
              </>
            )}
          </div>
        </div>
      )}

      {shareModal && (
        <div className="modal-overlay" onClick={handleCloseShareModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseShareModal}><X size={16} /></button>
            <h2 className="modal-title" style={{ marginBottom: '1rem' }}>{t("share_title")}</h2>
            {shareModal.report && (() => {
              const msg = t("share_message", shareModal.report.score, shareModal.report.badge, shareModal.url);
              return (
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: '0.5rem' }}>{t("share_message_label")}</p>
                  <div style={{ background: "var(--panel-deep)", borderRadius: '0.625rem', padding: "0.625rem 0.875rem", fontSize: '0.8125rem', color: "var(--text)", lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: '0.5rem' }}>
                    {msg}
                  </div>
                  <button
                    className="cta cta-primary wide"
                    style={{ fontSize: '0.8125rem' }}
                    onClick={() => { navigator.clipboard.writeText(msg); showToast(t("toast_copied")); }}
                  >
                    {t("copy_message")}
                  </button>
                </div>
              );
            })()}
            <p style={{ fontSize: '0.8125rem', color: "var(--muted)", marginBottom: '0.5rem' }}>{t("link_only")}</p>
            <div className="share-link-box">
              <span className="share-link-text">{shareModal.url}</span>
              <button
                className="cta cta-ghost"
                style={{ fontSize: '0.75rem', padding: "0.375rem 0.75rem", flexShrink: 0 }}
                onClick={() => {
                  navigator.clipboard.writeText(shareModal.url);
                  showToast(t("toast_link_copied"));
                }}
              >
                {t("copy")}
              </button>
            </div>
            <div style={{ display: "flex", gap: '0.5rem', marginTop: '0.75rem', flexWrap: "wrap" }}>
              <button
                className="cta cta-ghost wide"
                style={{ borderColor: "rgba(29,155,240,0.4)", color: "#1d9bf0" }}
                onClick={() => {
                  const text = t("share_message", shareModal.report?.score, shareModal.report?.badge, shareModal.url);
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
                }}
              >
                𝕏 Twitter
              </button>
              <button
                className="cta cta-ghost wide"
                style={{ borderColor: "rgba(88,101,242,0.4)", color: "#7289da" }}
                onClick={() => {
                  const text = t("discord_message", shareModal.report?.player, shareModal.report?.score, shareModal.report?.badge, shareModal.url);
                  navigator.clipboard.writeText(text);
                  showToast(t("discord_copied"));
                }}
              >
                Discord
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal && (
        <div className="modal-overlay" onClick={() => { setResetModal(null); setResetDone(false); setResetError(""); }}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setResetModal(null); setResetDone(false); }}><X size={16} /></button>
            {!resetDone ? (
              <>
                <h2 className="modal-title">{t("new_password_title")}</h2>
                <input className="rm-input" style={{ width: "100%", marginBottom: '0.625rem', boxSizing: "border-box" }} type="password" placeholder={t("pw_new_label")} value={resetForm.next} onChange={(e) => setResetForm((f) => ({ ...f, next: e.target.value }))} autoFocus />
                <input className="rm-input" style={{ width: "100%", marginBottom: '0.625rem', boxSizing: "border-box" }} type="password" placeholder={t("pw_confirm_label")} value={resetForm.confirm} onChange={(e) => setResetForm((f) => ({ ...f, confirm: e.target.value }))} />
                <div className="pwd-criteria" style={{ marginBottom: '0.625rem' }}>
                  {[
                    [t("criteria_8chars"), resetForm.next.length >= 8],
                    [t("criteria_uppercase"), /[A-Z]/.test(resetForm.next)],
                    [t("criteria_number"), /[0-9]/.test(resetForm.next)],
                    [t("criteria_special"), /[^A-Za-z0-9]/.test(resetForm.next)],
                  ].map(([label, ok]) => (
                    <span key={label} className={`pwd-criterion${ok ? " ok" : ""}`}>{ok ? "✓" : "·"} {label}</span>
                  ))}
                </div>
                {resetError && <p className="settings-error" style={{ marginBottom: '0.5rem' }}>{resetError}</p>}
                <button className="cta cta-primary wide" onClick={handleResetPassword} disabled={resetLoading}>
                  {resetLoading ? "..." : t("pw_reset_btn")}
                </button>
              </>
            ) : (
              <>
                <div style={{ textAlign: "center", marginBottom: '0.625rem' }}><CheckCircle size={40} style={{ color: "var(--gold)" }} /></div>
                <h2 className="modal-title" style={{ textAlign: "center" }}>{t("pw_updated_title")}</h2>
                <p style={{ fontSize: '0.8125rem', color: "var(--muted)", textAlign: "center", marginBottom: '1rem' }}>{t("pw_updated_sub")}</p>
                <button className="cta cta-primary wide" onClick={() => { setResetModal(null); setResetDone(false); setShowLogin(true); }}>
                  {t("login_btn")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {settingsModal && (
        <div className="modal-overlay" onClick={() => { setSettingsModal(false); setDeleteConfirm(false); setCancelSubConfirm(false); setPwdError(""); }}>
          <div className="modal-box settings-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setSettingsModal(false); setDeleteConfirm(false); setCancelSubConfirm(false); }}><X size={16} /></button>
            <h2 className="modal-title">{t("settings_title")}</h2>

            <div className="modal-tabs">
              {["account", "password", "premium", "visuel"].map((tab) => (
                <button key={tab} className={`modal-tab${settingsTab === tab ? " active" : ""}`} onClick={() => { setSettingsTab(tab); setPwdError(""); setDeleteConfirm(false); setCancelSubConfirm(false); }}>
                  {tab === "account" ? t("tab_account") : tab === "password" ? t("tab_password") : tab === "premium" ? t("tab_premium") : t("tab_visual")}
                </button>
              ))}
            </div>

            {settingsTab === "premium" && (
              <div className="settings-section">
                <div className="plans-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: '0.625rem', marginBottom: '1rem' }}>
                  {[
                    {
                      label: t("plan_no_account"),
                      price: t("plan_free_price"),
                      color: "var(--muted)",
                      quota: t("quota_anon"),
                      features: [
                        { text: t("feature_history"), ok: false },
                        { text: t("feature_suggestions"), ok: false },
                        { text: t("feature_roast_levels"), ok: false },
                        { text: t("feature_share"), ok: true },
                        { text: t("feature_themes"), ok: false },
                        { text: t("feature_squad"), ok: false },
                        { text: t("feature_community"), ok: false },
                        { text: t("feature_referrals_none"), ok: false },
                      ],
                    },
                    {
                      // Facturer ce qui coute, offrir ce qui attache : historique,
                      // themes et profil communaute sont cumulatifs et quasi gratuits
                      // a servir. Seuls les consommables (Squad Ranking, Coach)
                      // restent derriere le pass.
                      label: t("plan_free"),
                      price: t("plan_free_account_price"),
                      color: "var(--text)",
                      quota: t("quota_free"),
                      features: [
                        { text: t("feature_history"), ok: true },
                        { text: t("feature_suggestions"), ok: true },
                        { text: t("feature_roast_levels"), ok: true },
                        { text: t("feature_share"), ok: true },
                        { text: t("feature_themes"), ok: true },
                        { text: t("feature_squad"), ok: false },
                        { text: t("feature_community"), ok: true },
                        { text: t("feature_referrals_link"), ok: true },
                      ],
                    },
                    {
                      label: t("plan_premium"),
                      price: t("plan_premium_price"),
                      color: "var(--gold)",
                      highlight: true,
                      active: !!user?.premium,
                      quota: t("quota_premium"),
                      features: [
                        { text: t("feature_history"), ok: true },
                        { text: t("feature_suggestions"), ok: true },
                        { text: t("feature_roast_levels"), ok: true },
                        { text: t("feature_share"), ok: true },
                        { text: t("feature_themes"), ok: true },
                        { text: t("feature_squad"), ok: true },
                        { text: t("feature_community"), ok: true },
                        { text: t("feature_referrals_link"), ok: true },
                      ],
                    },
                  ].map((plan) => (
                    <div key={plan.label} style={{
                      borderRadius: '0.75rem',
                      border: plan.highlight ? "2px solid var(--gold)" : "1px solid var(--border)",
                      background: plan.highlight ? "var(--gold-a06)" : "var(--panel-deep)",
                      padding: "0.875rem 0.75rem",
                      display: "flex",
                      flexDirection: "column",
                    }}>
                      <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: plan.color, marginBottom: 2 }}>
                        {plan.label}
                        {plan.active && <span style={{ marginLeft: '0.3125rem', background: "var(--gold)", color: "#000", borderRadius: '0.25rem', padding: "1px 0.3125rem", fontSize: '0.5625rem' }}>{t("active_badge")}</span>}
                      </div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 800, color: plan.color, marginBottom: '0.625rem' }}>{plan.price}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: '0.375rem' }}>
                        {[{ text: plan.quota, ok: true }, ...plan.features].map((f) => (
                          <div key={f.text} style={{ display: "flex", gap: '0.375rem', alignItems: "flex-start", fontSize: '0.6875rem', color: f.ok ? "var(--text)" : "var(--muted)", opacity: f.ok ? 1 : 0.4 }}>
                            <span style={{ color: f.ok ? "var(--gold)" : "var(--muted)", flexShrink: 0 }}>{f.ok ? "✓" : "✗"}</span>
                            {f.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {user?.is_subscribed ? (
                  <div style={{ marginTop: '1rem' }}>
                    {!cancelSubConfirm ? (
                      <button className="cta cta-ghost wide" style={{ fontSize: '0.8125rem', borderColor: "rgba(255,80,80,0.4)", color: "#ff8080" }} onClick={() => setCancelSubConfirm(true)}>
                        {t("cancel_subscription")}
                      </button>
                    ) : (
                      <div className="delete-confirm">
                        <p className="settings-error">{t("cancel_sub_confirm_msg")}</p>
                        <div style={{ display: "flex", gap: '0.5rem', marginTop: '0.5rem' }}>
                          <button className="cta cta-ghost wide" onClick={() => setCancelSubConfirm(false)}>{t("cancel")}</button>
                          <button className="cta wide" style={{ background: "rgba(255,60,60,0.2)", border: "1px solid rgba(255,60,60,0.5)", color: "#ff6b6b" }} onClick={() => { setCancelSubConfirm(false); handleCancelSubscription(); }}>
                            {t("cancel_sub_confirm_btn")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : user?.pass_active ? (
                  // Aucun bouton d'achat pendant un pass ouvert : vendre un acces de
                  // vingt-quatre heures a quelqu'un qui en a deja un n'a pas de sens,
                  // et le pass n'est plus cumulable.
                  <div style={{ marginTop: '1rem', padding: "0.75rem 0.875rem", background: "var(--gold-a06)", borderRadius: '0.625rem', border: "1px solid var(--gold-a40)" }}>
                    <p style={{ fontSize: '0.8125rem', color: "var(--gold)", margin: 0, fontWeight: 700 }}>
                      {t("pass_ongoing", user.pass_hours_remaining)}
                    </p>
                  </div>
                ) : (
                  <div style={{ marginTop: '1rem', display: "flex", flexDirection: "column", gap: '0.5rem' }}>
                    <button className="cta cta-primary wide" style={{ fontSize: '0.8125rem' }} onClick={() => { setSettingsModal(false); handleSubscribe(); }}>
                      {t("upgrade_premium")}
                    </button>
                  </div>
                )}
                {user?.referral_code && (
                  <div style={{ marginTop: '1.25rem', padding: "0.75rem 0.875rem", background: "var(--panel-deep)", borderRadius: '0.625rem', border: "1px solid var(--border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: '0.375rem' }}>
                      <p style={{ fontSize: '0.75rem', color: "var(--gold)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>{t("referral_title")}</p>
                      <button className="cta cta-ghost" style={{ fontSize: '0.6875rem', padding: "0.1875rem 0.5rem", flexShrink: 0 }}
                        onClick={async () => {
                          const res = await fetch(`${API}/auth/referral/regenerate`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("rms_token")}` } });
                          if (res.ok) {
                            const data = await res.json();
                            setUser((u) => ({ ...u, referral_code: data.referral_code }));
                            showToast(t("new_link_generated"));
                          }
                        }}>
                        {t("regenerate")}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: "var(--muted)", marginBottom: '0.5rem' }}>{t("referral_sub")}</p>
                    <div className="share-link-box">
                      <span className="share-link-text" style={{ fontSize: '0.75rem' }}>{window.location.origin}?ref={user.referral_code}</span>
                      <button className="cta cta-ghost" style={{ fontSize: '0.6875rem', padding: "0.3125rem 0.625rem", flexShrink: 0 }}
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}?ref=${user.referral_code}`); showToast(t("link_copied")); }}>
                        {t("copy")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {settingsTab === "password" && (
              <div className="settings-section">
                <input className="rm-input" style={{ width: "100%", marginBottom: '0.625rem' }} type="password" placeholder={t("pw_current")} value={pwdForm.current} onChange={(e) => setPwdForm((f) => ({ ...f, current: e.target.value }))} />
                <input className="rm-input" style={{ width: "100%", marginBottom: '0.625rem' }} type="password" placeholder={t("pw_new_placeholder")} value={pwdForm.next} onChange={(e) => setPwdForm((f) => ({ ...f, next: e.target.value }))} />
                <input className="rm-input" style={{ width: "100%", marginBottom: '0.625rem' }} type="password" placeholder={t("pw_confirm_placeholder")} value={pwdForm.confirm} onChange={(e) => setPwdForm((f) => ({ ...f, confirm: e.target.value }))} />
                <div className="pwd-criteria">
                  {[
                    [t("criteria_8chars"), pwdForm.next.length >= 8],
                    [t("criteria_uppercase"), /[A-Z]/.test(pwdForm.next)],
                    [t("criteria_number"), /[0-9]/.test(pwdForm.next)],
                    [t("criteria_special"), /[^A-Za-z0-9]/.test(pwdForm.next)],
                  ].map(([label, ok]) => (
                    <span key={label} className={`pwd-criterion${ok ? " ok" : ""}`}>
                      {ok ? "✓" : "·"} {label}
                    </span>
                  ))}
                </div>
                {pwdError && <p className="settings-error">{pwdError}</p>}
                <button className="cta cta-primary wide" style={{ marginTop: '0.75rem' }} onClick={handleChangePassword} disabled={pwdLoading}>
                  {pwdLoading ? "..." : t("change_password")}
                </button>
              </div>
            )}

            {settingsTab === "account" && (
              <div className="settings-section">
                {user?.firstname && (
                  <div className="account-info">
                    <span className="settings-label">{t("label_name")}</span>
                    <span className="settings-value">{user.firstname}</span>
                  </div>
                )}
                <div className="account-info">
                  <span className="settings-label">{t("label_email")}</span>
                  <span className="settings-value">{user?.email}</span>
                </div>
                <div className="account-info">
                  <span className="settings-label">{t("label_subscription")}</span>
                  <span className="settings-value">{user?.is_subscribed ? t("sub_premium") : t("sub_free")}</span>
                </div>
                <div className="account-info">
                  <span className="settings-label">{t("label_language")}</span>
                  <div style={{ display: "flex", gap: '0.375rem' }}>
                    <button className={`cta ${lang === "fr" ? "cta-primary" : "cta-ghost"}`} style={{ fontSize: '0.6875rem', padding: "0.1875rem 0.625rem" }} onClick={() => setLanguage("fr")}>FR</button>
                    <button className={`cta ${lang === "en" ? "cta-primary" : "cta-ghost"}`} style={{ fontSize: '0.6875rem', padding: "0.1875rem 0.625rem" }} onClick={() => setLanguage("en")}>EN</button>
                  </div>
                </div>
                <hr className="settings-hr" />
                {!deleteConfirm ? (
                  <button className="cta wide" style={{ background: "rgba(255,60,60,0.12)", border: "1px solid rgba(255,60,60,0.35)", color: "#ff6b6b", marginTop: '0.5rem' }} onClick={() => setDeleteConfirm(true)}>
                    {t("delete_account")}
                  </button>
                ) : (
                  <div className="delete-confirm">
                    <p className="settings-error">{t("delete_confirm_msg")}</p>
                    <div style={{ display: "flex", gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button className="cta cta-ghost wide" onClick={() => setDeleteConfirm(false)}>{t("cancel")}</button>
                      <button className="cta wide" style={{ background: "rgba(255,60,60,0.2)", border: "1px solid rgba(255,60,60,0.5)", color: "#ff6b6b" }} onClick={handleDeleteAccount}>
                        {t("delete_confirm_btn")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {settingsTab === "visuel" && (() => {
              const curMode = theme.mode || "dark";
              const curAccent = theme.accent || "gold";
              const curBg = theme.background || "default";
              const accentColors = { gold: "#c89b3c", blue: "#0bc4e3", purple: "#9b59b6", red: "#e74c3c" };
              const label12 = { fontSize: '0.6875rem', color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: '0.625rem', display: "block" };
              const themeBtn = (active) => ({
                flex: 1, fontSize: '0.8125rem', padding: "0.625rem 0.5rem", cursor: "pointer", borderRadius: '0.625rem',
                border: active ? "2px solid var(--gold)" : "1px solid var(--border)",
                background: active ? "rgba(var(--gold-rgb,200,155,60),0.15)" : "transparent",
                color: active ? "var(--gold)" : "var(--muted)", fontWeight: active ? 700 : 500,
                transition: "all 0.2s",
              });
              return (
                <div className="settings-section">
                  {/* Le theme est cumulatif, pas consommable : il est ouvert a tout
                      compte connecte depuis le 2026-09-09. */}
                  <div>

                    <span style={label12}>{t("mode_label")}</span>
                    <div style={{ display: "flex", gap: '0.5rem', marginBottom: '1.25rem' }}>
                      <button style={themeBtn(curMode === "dark")} onClick={() => updateTheme({ mode: "dark" })}>
                        <div style={{ width: '2rem', height: '1.125rem', borderRadius: '0.25rem', background: "#010a13", border: "1px solid rgba(255,255,255,0.15)", margin: "0 auto 0.375rem" }} />
                        {t("mode_dark")}
                      </button>
                      <button style={themeBtn(curMode === "light")} onClick={() => updateTheme({ mode: "light" })}>
                        <div style={{ width: '2rem', height: '1.125rem', borderRadius: '0.25rem', background: "linear-gradient(135deg,#dce8f5,#f0f4f8)", border: "1px solid rgba(0,0,0,0.1)", margin: "0 auto 0.375rem" }} />
                        {t("mode_light")}
                      </button>
                    </div>

                    <span style={label12}>{t("accent_label")}</span>
                    <div style={{ display: "flex", gap: '0.5rem', marginBottom: '1.25rem' }}>
                      {Object.entries(accentColors).map(([val, color]) => (
                        <button key={val} style={themeBtn(curAccent === val)} onClick={() => updateTheme({ accent: val })}>
                          <div style={{ width: '1.25rem', height: '1.25rem', borderRadius: "50%", background: color, margin: "0 auto 0.375rem", boxShadow: curAccent === val ? `0 0 8px ${color}` : "none" }} />
                          {val === "gold" ? t("accent_gold") : val === "blue" ? t("accent_blue") : val === "purple" ? t("accent_purple") : t("accent_red")}
                        </button>
                      ))}
                    </div>

                    <span style={label12}>{t("bg_label")}</span>
                    <div style={{ display: "flex", gap: '0.5rem' }}>
                      <button style={themeBtn(curBg === "default")} onClick={() => updateTheme({ background: "default" })}>
                        <div style={{
                          width: '2rem', height: '1.125rem', borderRadius: '0.25rem', margin: "0 auto 0.375rem",
                          background: curMode === "light"
                            ? "linear-gradient(135deg, #b8ccdf 0%, #dce8f5 50%, #ccdae8 100%)"
                            : "radial-gradient(circle at 30% 30%, #1a3050 0%, #0a1a2e 60%, #010a13 100%)",
                          border: curMode === "light" ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.08)",
                        }} />
                        {t("bg_default")}
                      </button>
                      <button style={themeBtn(curBg === "minimal")} onClick={() => updateTheme({ background: "minimal" })}>
                        <div style={{
                          width: '2rem', height: '1.125rem', borderRadius: '0.25rem', margin: "0 auto 0.375rem",
                          background: curMode === "light" ? "#eef2f7" : "#010a13",
                          border: curMode === "light" ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.08)",
                        }} />
                        {t("bg_minimal")}
                      </button>
                      <button style={themeBtn(curBg === "animated")} onClick={() => updateTheme({ background: "animated" })}>
                        <div style={{
                          width: '2rem', height: '1.125rem', borderRadius: '0.25rem', margin: "0 auto 0.375rem",
                          background: curMode === "light"
                            ? "linear-gradient(90deg, #b8ccdf, #dce8f5, #ccd4e8, #dce8f5, #b8ccdf)"
                            : "linear-gradient(90deg, #1a0d2e, #0d1e35, #050e1a, #2e0d1a)",
                          border: curMode === "light" ? "1px solid rgba(0,0,0,0.12)" : "1px solid rgba(255,255,255,0.08)",
                          backgroundSize: "300% 100%", animation: "gradientShift 3s ease infinite",
                        }} />
                        {t("bg_animated")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {adminOpen && <AdminDashboard token={token} onClose={() => setAdminOpen(false)} unread={adminUnread} onInboxChange={refreshAdminUnread} />}

      {showLogin && <LoginModal onClose={() => { setShowLogin(false); setLoginTab("login"); }} onLogin={handleLogin} t={t} lang={lang} initialTab={loginTab} />}

      {/* Pile de notifications : l'empilement est fait par le CSS (.toast-stack),
          plus par un `bottom` calcule sur une hauteur supposee de la carte d'avis. */}
      <div className="toast-stack">
      {showFeedbackToast && (
        <FeedbackToast onClose={() => setShowFeedbackToast(false)} t={t} />
      )}

      {upsellNotif && !isPremium && (
        <div style={{
          background: "var(--panel)", border: "1px solid var(--gold-a40)",
          borderRadius: '0.875rem', padding: "0.875rem 1rem", maxWidth: '18.75rem',
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          display: "flex", flexDirection: "column", gap: '0.625rem',
          animation: "toastIn 0.3s ease",
        }}>
          <div style={{ display: "flex", gap: '0.625rem', alignItems: "flex-start" }}>
            <Crown size={16} style={{ flexShrink: 0, color: "var(--gold)", marginTop: 1 }} />
            <span style={{ fontSize: '0.8125rem', color: "var(--text)", lineHeight: 1.5, flex: 1 }}>
              {upsellNotif.msgFn(t)}
            </span>
            <button
              onClick={() => setUpsellNotif(null)}
              style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 0, flexShrink: 0, lineHeight: 1, display: "flex" }}
            >
              <X size={14} />
            </button>
          </div>
          <button
            className="cta cta-primary"
            style={{ fontSize: '0.75rem', padding: "0.4375rem 0.875rem", alignSelf: "flex-start" }}
            onClick={() => { setUpsellNotif(null); upsellNotif.isAnon ? setShowLogin(true) : handleSubscribe(); }}
          >
            {upsellNotif.isAnon ? t("upsell_create_account") : t("upsell_go_premium")}
          </button>
        </div>
      )}

      {toast && (
        <div className={`toast${toast.type === "error" ? " toast-error" : ""}`}>
          {toast.type === "error" ? <X size={14} style={{ flexShrink: 0 }} /> : <Check size={14} style={{ flexShrink: 0 }} />} {toast.msg}
        </div>
      )}
      </div>

      {/* Footer */}
      <footer className="site-footer">
        {/* Badge Product Hunt — retire le 13 septembre 2026, le lancement n'ayant rien
            donne. Conserve ici pour un eventuel prochain passage : le rallumer demande
            aussi de rouvrir img-src a api.producthunt.com (vercel.json) et de
            redeclarer Product Hunt dans legal.js, sinon l'image est bloquee sans erreur.
        {phasePH() === "live" && (
          <a
            href={PH_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "inline-block", marginBottom: '1rem' }}
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1247890&theme=dark"
              alt="Roast My Stats - Let an AI brutally destroy your gaming pride. | Product Hunt"
              width="250"
              height="54"
              style={{ width: '15.625rem', height: '3.375rem' }}
            />
          </a>
        )}
        */}
        <p className="footer-seo">{t("seo_intro")}</p>
        <p className="footer-disclaimer">
          Roast My Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends. League of Legends and Riot Games are trademarks or registered trademarks of Riot Games, Inc.
        </p>
        <div className="footer-links">
          <button className="footer-link" onClick={() => setLegalModal("privacy")}>{t("privacy_policy")}</button>
          <span className="footer-sep">·</span>
          <button className="footer-link" onClick={() => setLegalModal("cgu")}>{t("cgu")}</button>
          <span className="footer-sep">·</span>
          <button className="footer-link" onClick={() => setLegalModal("mentions")}>{t("legal_notice")}</button>
          <span className="footer-sep">·</span>
          <button className="footer-link" onClick={() => setLegalModal("contact")}>{t("contact")}</button>
          <span className="footer-sep">·</span>
          <span className="footer-copy">© {new Date().getFullYear()} Roast My Stats</span>
        </div>
      </footer>

      {/* Modales juridiques — contenu et traductions dans legal.js */}
      {(legalModal === "privacy" || legalModal === "cgu" || legalModal === "mentions") && (
        <div className="modal-overlay" onClick={() => setLegalModal(null)}>
          <div className="modal-box legal-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setLegalModal(null)}><X size={16} /></button>
            {showLogin && <button className="legal-back-btn" onClick={() => setLegalModal(null)}>{t("legal_back")}</button>}
            <h2 className="modal-title">
              {legalModal === "privacy" ? t("privacy_modal_title")
                : legalModal === "cgu" ? t("cgu_modal_title")
                : t("legal_notice")}
            </h2>
            <LegalDocument doc={legalModal} lang={lang} />
          </div>
        </div>
      )}

      {/* Modal Contact */}
      {legalModal === "contact" && (
        <ContactModal
          token={token}
          user={user}
          onClose={() => setLegalModal(null)}
          showToast={showToast}
          t={t}
        />
      )}

    </div>
  );
}

export default App;
